// ── 직원 편집 훅 — 직원 추가/수정/비번초기화/퇴직 처리 로직 ─────────────────
import { useState } from 'react';
import { apiClient } from '../../../api/apiClient';
import { todayKST } from '../../../utils/date';
import { useUserLifecycle } from './useUserLifecycle';
import { marketerTerminalFields, addHqTerminal, saveCompanyTerminal } from './marketerTerminal';
import { emptyForm, isNoId, normalizeAttendanceTimeType } from './utils';
import { editFormFromUser, buildSavePayload } from './userEditPayload';
import { isFreelancerWorker } from '../../../utils/workerType';

/**
 * 직원 수정 모달 상태 + 액션 훅
 * @param {object} params
 *   showToast             — 토스트 알림 함수
 *   attendanceTypeOptions — 출근 유형 선택 목록
 *   departments           — 부서 선택 목록
 *   ipAliases             — 기본 근무지 IP 별칭 목록
 *   apiBase               — API 기본 경로 (/api/admin/users 등)
 *   entityLabel           — 엔티티 명칭 (직원 | 외부직원)
 *   fetchUsers            — 목록 새로 고침 콜백
 */
export function useUserEdit({
  showToast, attendanceTypeOptions, departments, ipAliases, apiBase, entityLabel, fetchUsers,
  onMarketerCreated, marketerCompanyCode, canCreateFreelancer = false,
}) {
  // ── 마케터(3.3%, worker_type='freelancer' — 옛 'sales') 행 라우팅 (2026-09-07 0단계①) ──────────────
  //   테넌트 스코프 직원 목록에는 마케터가 섞이는데, employee 라우트는 서버의
  //   worker_type 게이트에 걸려 수정·비번초기화·퇴직처리가 전부 404였다.
  //   서버 게이트를 완화하면 명부 권한(SALES_ROSTER_MANAGEMENT) 우회·부서 강제 상실·
  //   감사 축 왜곡이 생기므로, 프론트가 마케터 행만 명부 라우트로 보낸다.
  const SALES_WRITE_BASE = '/api/admin/sales-people';
  const isSalesRow = (row) => isFreelancerWorker(row?.worker_type); // 3.3%(프리랜서) 판정
  const writeBaseFor = (row) => (isSalesRow(row) ? SALES_WRITE_BASE : apiBase);
  // 조회도 같은 갈래로 — 마케터는 company_code 가 PINE 고정이라 DOT/LNJ 탭의 ext 상세
  //   라우트로 읽으면 404 가 난다. 회사별 마케터 탭에서 [수정] 이 열리게 하는 조건 (2026-09-08).
  const readBaseFor = (row) => (isSalesRow(row) ? SALES_WRITE_BASE : apiBase);

  // 모달 열림 여부 + 편집 대상 직원 데이터
  const [open, setOpen]       = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm]       = useState(emptyForm());

  // 비번초기화·퇴직처리·주소검색 (분리됨 — 마케터 행 라우팅은 writeBaseFor 로 그대로 이어진다)
  const lifecycle = useUserLifecycle({ showToast, writeBaseFor, fetchUsers, setForm });

  // ── 기본 출근 유형 계산 ───────────────────────────────────────────────────
  function getDefaultAttendanceType() {
    return normalizeAttendanceTimeType('', attendanceTypeOptions);
  }

  // ── 신규 등록 창 열기 ────────────────────────────────────────────────────
  //   2026-11-03 한 목록으로 합침 — 창 맨 위 '구분'(create_kind: 'insured' 4대보험 / 'freelance' 3.3%)을 매번 고른다(결정 3b, 비어서 시작).
  //   3.3% = 명부 라우트 POST + 지금 보는 회사 담당으로 자동 연결(예전 [+ 마케터 등록]과 같은 길).
  function openCreate() {
    setEditing(null);
    setForm({
      ...emptyForm(),
      create_kind: '',
      attendance_time_type: getDefaultAttendanceType(),
      original_attendance_time_type: getDefaultAttendanceType(),
      work_schedule_effective_from: todayKST(),
      department_effective_from: todayKST(),
      bank_effective_from: todayKST(),
    });
    setOpen(true);
  }

  // ── 직원 수정 모달 열기 ──────────────────────────────────────────────────
  async function openEdit(row) {
    const res = await apiClient.get(`${readBaseFor(row)}/${encodeURIComponent(row.id)}`);
    if (!res.ok || !res.data?.user) {
      showToast(`최신 ${entityLabel} 정보를 불러오지 못했습니다. 새로고침 후 다시 시도해 주세요.`, 'error');
      return;
    }
    const latest = res.data.user;
    setEditing(latest);
    setForm(editFormFromUser(latest, {
      attendanceTypeOptions,
      terminalFields: marketerTerminalFields({ apiBase, companyCode: marketerCompanyCode, row, latest }),
    }));
    setOpen(true);
  }

  // ── 직원 저장 (생성 또는 수정) ───────────────────────────────────────────
  async function saveUser() {
    // 이름·부서 필수 — 2026-11-03 합치기-2 부터 3.3% 도 부서를 고른다(테넌트의 3.3% 부서 목록, 결정 5b).
    //   (예전 2026-09-09: 서버가 3.3% 부서를 '마케팅'으로 고정해서 3.3% 는 이름만 필수였다.)
    // 등록은 구분부터(결정 3b) — 3.3% 는 고를 자격이 있을 때만(화면에서도 막지만 한 번 더)
    if (!editing && form.create_kind !== 'insured' && form.create_kind !== 'freelance') {
      showToast('구분(4대보험 / 3.3%)을 골라 주세요.', 'error');
      return;
    }
    if (!editing && form.create_kind === 'freelance' && !canCreateFreelancer) {
      showToast('3.3% 인원은 그 회사의 직원 관리 권한이 있어야 등록할 수 있습니다.', 'error');
      return;
    }
    const createAsMarketer = !editing && form.create_kind === 'freelance';
    const marketerRow = editing ? isSalesRow(editing) : createAsMarketer;
    // 검사 순서·알림 문구·보내는 칸은 userEditPayload.js (2026-11-03 옮김, 동작 같음)
    const built = buildSavePayload({ form, editing, marketerRow, attendanceTypeOptions });
    if (built.error) {
      showToast(built.error, 'error');
      return;
    }
    const { payload } = built;

    // 아이디 없음 상태에서 최초 아이디 설정
    if (editing && isNoId(editing.user_id) && form.userId) {
      const idSet = await apiClient.patch(`${writeBaseFor(editing)}/${editing.id}/set-user-id`, { userId: form.userId });
      if (!idSet.ok) {
        showToast(idSet.data?.message || '아이디 설정에 실패했습니다.', 'error');
        return;
      }
    }

    // 퇴직일을 비웠을 때(=퇴직 취소): 급여프로필·인센티브 배정을 함께 복구할지 명시적 동의를 받는다.
    // 동의하지 않으면 비활성 상태 그대로 두고 사용자 정보만 저장한다(잘못 누른 경우 안전).
    const hadResignDate = Boolean(editing?.resign_date);
    const clearedResignDate = hadResignDate && !payload.resign_date;
    if (clearedResignDate) {
      const ok = window.confirm(
        '퇴직일을 비웠습니다. 이 직원의 급여프로필과 인센티브 배정도 다시 활성화할까요?\n\n'
        + '확인: 퇴직 시 자동으로 비활성된 급여프로필과 인센티브 배정을 함께 복구합니다.\n'
        + '취소: 사용자 정보만 저장합니다. (급여프로필·인센티브는 비활성 상태 유지)'
      );
      if (ok) payload.restore_payroll_profile_on_resign_cancel = true;
    }

    // 마케터: 근무유형·연차 계열 필드는 보내지 않는다 — 마케터는 연차·출퇴근 대상이
    //   아니며(4겹 방화벽의 D겹), 명부 폼에도 원래 없는 값이다. 저장은 명부 라우트로.
    const marketer = editing ? isSalesRow(editing) : createAsMarketer;
    if (marketer) {
      delete payload.attendance_time_type;
      delete payload.work_schedule_effective_from;
      delete payload.work_schedule_change_mode;
      delete payload.work_schedule_reason;
      delete payload.first_year_grant_count;
      addHqTerminal(payload, { apiBase, form });
    }

    const createBase = createAsMarketer ? SALES_WRITE_BASE : apiBase;
    // 마케터 등록에는 지금 보고 있는 회사 코드를 함께 보낸다 (2026-09-09).
    //   서버가 "이 사람이 그 회사의 직원관리 담당자인가" 를 확인하는 데 쓴다.
    //   ⚠ 권한 판정·감사 전용이다 — users.company_code 로 저장되지 않는다(마케터는 전원 PINE).
    const createExtra = createAsMarketer && marketerCompanyCode ? { company_code: marketerCompanyCode } : {};
    const result = editing
      ? await apiClient.put(`${writeBaseFor(editing)}/${editing.id}`, payload)
      : await apiClient.post(createBase, { userId: form.userId, ...payload, ...createExtra });

    if (!result.ok) {
      showToast(result.data?.message || '저장에 실패했습니다.', 'error');
      return;
    }

    // 마케터를 새로 등록했으면 지금 보고 있는 회사 담당으로 바로 연결한다.
    //   (연결이 없으면 등록해도 어느 탭에도 안 보여 "등록했는데 사라졌다"가 된다)
    const newId = result.data?.user?.id || result.data?.id;
    if (!editing && createAsMarketer && newId && onMarketerCreated) await onMarketerCreated(newId);
    if (marketer) { const tErr = await saveCompanyTerminal({ apiBase, companyCode: marketerCompanyCode, userId: editing?.id || newId, form }); if (tErr) showToast(tErr, 'error'); }

    const label = marketer ? '마케터' : entityLabel;
    showToast(editing ? `${label} 정보를 수정했습니다.` : `${label}을 등록했습니다.`, 'success');
    setOpen(false);
    fetchUsers();
  }

  // ── 비밀번호 초기화 ──────────────────────────────────────────────────────
  async function cancelFutureWorkSchedule() {
    const next = editing?.next_work_schedule;
    if (!editing?.id || !next?.id) {
      showToast('취소할 근무유형 예약이 없습니다.', 'error');
      return;
    }
    const nextType = normalizeAttendanceTimeType(next.attendance_time_type, attendanceTypeOptions) || '출퇴근 없음';
    if (!window.confirm(`${next.effective_from}부터 적용 예정인 "${nextType}" 근무유형 예약을 취소할까요?`)) return;

    const result = await apiClient.delete(`${apiBase}/${editing.id}/work-schedule/${next.id}`);
    if (!result.ok || result.data?.success === false) {
      showToast(result.data?.message || '근무유형 예약 취소에 실패했습니다.', 'error');
      return;
    }

    showToast(result.data?.message || '근무유형 예약을 취소했습니다.', 'success');
    setOpen(false);
    fetchUsers();
  }

  return {
    open, setOpen,
    editing, form, setForm,
    attendanceTypeOptions, departments, ipAliases,
    openCreate, openEdit, saveUser,
    cancelFutureWorkSchedule,
    // 지금 편집 중인 대상이 마케터인가 — 모달이 안 쓰이는 칸을 숨기는 데 쓴다.
    isMarketerForm: editing ? isSalesRow(editing) : form.create_kind === 'freelance',
    canCreateFreelancer,
    ...lifecycle,
  };
}
